type OgType = 'website' | 'article' | 'product' | 'event';
type TwitterCard = 'summary' | 'summary_large_image';

export type SeoHead = {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  og: {
    title: string;
    description: string;
    url: string;
    type: OgType;
    image?: string;
    imageSecureUrl?: string;
    imageWidth?: string;
    imageHeight?: string;
    siteName?: string;
    locale?: string;
  };
  twitter: {
    card: TwitterCard;
    title: string;
    description: string;
    image?: string;
  };
  robots?: string;
  jsonLd?: Record<string, unknown> | null;
};

export type SeoRequestContext = {
  protocol: string;
  host: string;
  originalUrl: string;
  pathname: string;
  defaultImagePath: string;
  ogImageFallbackBaseUrl?: string;
  apiOrigin?: string;
};

export type SeoEventData = {
  slug: string;
  name: string;
  description?: string | null;
  coverImageUrl?: string | null;
  coverThumbLg?: string | null;
  coverThumbMd?: string | null;
  coverThumbSm?: string | null;
  coverThumbSquare?: string | null;
  coverThumbs?: {
    sm?: string;
    md?: string;
    lg?: string;
    square?: string;
  } | null;
  location?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  zipcode?: string | null;
  startAt?: string | null;
  endAt?: string | null;
  status?: 'draft' | 'published' | 'archived';
  type?: 'PRESENTIAL' | 'ONLINE' | 'HYBRID' | string | null;
  attendanceMode?: string | null;
  eventStatus?: string | null;
  organizerName?: string | null;
  organizerUrl?: string | null;
  category?: { name?: string | null } | null;
  batches?: Array<{
    price?: number | null;
    quantity?: number | null;
    sold?: number | null;
    status?: 'active' | 'sold_out' | 'pending' | string | null;
    startsAt?: string | null;
    endsAt?: string | null;
  }> | null;
};

export type SeoListingContext = {
  city?: string;
  category?: string;
};

const STOPWORDS = new Set([
  'a',
  'as',
  'o',
  'os',
  'de',
  'da',
  'das',
  'do',
  'dos',
  'e',
  'em',
  'na',
  'nas',
  'no',
  'nos',
  'para',
  'por',
  'com',
  'sem',
  'ao',
  'aos',
  'um',
  'uma',
  'uns',
  'umas',
]);

const TRACKING_QUERY_KEYS = new Set([
  'gclid',
  'fbclid',
  'msclkid',
  'dclid',
  'igshid',
  'mc_cid',
  'mc_eid',
  'ref',
  'source',
]);

const DEFAULT_TITLE = 'AccessHub | Plataforma de eventos e ingressos online';
const DEFAULT_DESCRIPTION =
  'Encontre eventos, shows e experiências perto de você. Compre ingressos online com rapidez e segurança no AccessHub.';

export const DEFAULT_SITE_ORIGIN = 'https://www.ticketaccesshub.com';
export const SEO_READY_META = 'accesshub-seo-ready';

// Only deployment configuration may select an origin. Request headers are not SEO input.
export function configuredSiteOrigin(value: string): string {
  try {
    const url = new URL(value);
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password
    ) {
      return DEFAULT_SITE_ORIGIN;
    }
    return url.origin;
  } catch {
    return DEFAULT_SITE_ORIGIN;
  }
}

export function createSeoContext(
  originalUrl: string,
  siteBaseUrl: string,
  apiBaseUrl?: string
): SeoRequestContext {
  const origin = configuredSiteOrigin(siteBaseUrl);
  const site = new URL(origin);
  let apiOrigin: string | undefined;
  try {
    apiOrigin = apiBaseUrl ? new URL(apiBaseUrl).origin : undefined;
  } catch {
    apiOrigin = undefined;
  }
  return {
    protocol: site.protocol.slice(0, -1),
    host: site.host,
    originalUrl,
    pathname: originalUrl.split(/[?#]/)[0] || '/',
    defaultImagePath: '/assets/logo.png',
    ogImageFallbackBaseUrl: origin,
    apiOrigin,
  };
}

export function routeRobots(originalUrl: string): string {
  const [path, query = ''] = originalUrl.split(/[?#]/);
  const normalizedPath = path.replace(/\/$/, '') || '/';
  if (/^\/(?:tickets|baixar-ingressos|r)(?:\/|$)/.test(normalizedPath)) {
    return 'noindex,nofollow';
  }
  if (/^\/eventos(?:\/[^/]+){0,2}$/.test(normalizedPath)) {
    const params = new URLSearchParams(query);
    const filtered = Array.from(params.keys()).some(
      (key) => !isTrackingKey(key) && !!params.get(key)
    );
    // Free-text event locations cannot establish a unique city/UF identity.
    return filtered || normalizedPath !== '/eventos'
      ? 'noindex,follow'
      : 'index,follow';
  }
  if (
    [
      '/',
      '/sobre',
      '/ajuda',
      '/contato',
      '/organizadores',
      '/perguntas-frequentes',
      '/politica-de-privacidade',
      '/termos-de-uso',
      '/preferencias-de-cookies',
    ].includes(normalizedPath) ||
    /^\/(?:evento|e|event)\/[^/]+$/.test(normalizedPath)
  )
    return 'index,follow';
  return 'noindex,follow';
}

function isTrackingKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return normalized.startsWith('utm_') || TRACKING_QUERY_KEYS.has(normalized);
}

export function safeSocialImage(
  value: string,
  origin: string
): string | undefined {
  if (!value.trim()) return undefined;
  try {
    const url = new URL(value, configuredSiteOrigin(origin));
    if (
      !['http:', 'https:'].includes(url.protocol) ||
      url.username ||
      url.password
    )
      return undefined;
    return url.href;
  } catch {
    return undefined;
  }
}

export function buildEventSeo(
  eventData: SeoEventData,
  reqContext: SeoRequestContext
): SeoHead {
  const canonical = buildCanonical(reqContext);
  const city = normalizeText(eventData.city || '');
  const place = normalizeText(eventData.location || eventData.address || city);
  const dateLabel = formatDateShort(eventData.startAt);
  const banner = resolveImage(eventData);
  const eventImage = resolveEventImageUrl(banner, reqContext, {
    headline: eventData.name,
    subtitle: [dateLabel, place || city].filter(Boolean).join(' - '),
    cta: 'Compre ingressos',
  });
  const fallbackImage = buildFallbackOgImageUrl(eventData.slug, reqContext);
  const defaultImage = toAbsoluteUrl(reqContext.defaultImagePath, reqContext);
  const image = forceHttps(eventImage || fallbackImage || defaultImage);

  const title = buildEventTitle(eventData.name, city || place, dateLabel);
  const description = stripHtml(eventData.description || '')
    ? truncateWords(stripHtml(eventData.description || ''), 160)
    : buildEventDescription(eventData.name, place || city, dateLabel);
  const keywords = buildKeywords({
    eventName: eventData.name,
    city,
    category: normalizeText(eventData.category?.name || ''),
    location: place,
  });
  const robots =
    eventData.status === 'published' ? 'index,follow' : 'noindex,follow';
  const jsonLd =
    eventData.status === 'published'
      ? buildEventJsonLd(eventData, canonical, image)
      : null;

  return buildSeoHead({
    title,
    description,
    canonical,
    keywords,
    robots,
    ogType: 'event',
    image,
    jsonLd,
  });
}

export function buildListingSeo(
  listingCtx: SeoListingContext,
  reqContext: SeoRequestContext
): SeoHead {
  const canonical = buildCanonical(reqContext);
  const city = normalizeText(listingCtx.city || '');
  const category = normalizeText(listingCtx.category || '');
  const image = toAbsoluteUrl(reqContext.defaultImagePath, reqContext);

  const title = buildListingTitle(city, category);
  const description = buildListingDescription(city, category);
  const keywords = buildKeywords({
    eventName: `${category} ${city}`.trim(),
    city,
    category,
    location: city,
  });

  return buildSeoHead({
    title,
    description,
    canonical,
    keywords,
    robots: routeRobots(reqContext.originalUrl),
    ogType: 'website',
    image,
    jsonLd: null,
  });
}

export function buildDefaultSeo(reqContext: SeoRequestContext): SeoHead {
  const canonical = buildCanonical(reqContext);
  const image = toAbsoluteUrl(reqContext.defaultImagePath, reqContext);

  return buildSeoHead({
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    canonical,
    keywords: 'ingressos, eventos, AccessHub, ingresso online',
    robots: /^\/(?:evento|e|event)\//.test(reqContext.pathname)
      ? 'noindex,follow'
      : routeRobots(reqContext.originalUrl),
    ogType: 'website',
    image,
    jsonLd: null,
  });
}

export function renderSeoBlock(seo: SeoHead): string {
  const lines = [
    `<title>${escapeHtmlAttr(seo.title)}</title>`,
    `<meta name="description" content="${escapeHtmlAttr(seo.description)}" />`,
    `<meta name="robots" content="${escapeHtmlAttr(
      seo.robots || 'index,follow'
    )}" />`,
    `<link rel="canonical" href="${escapeHtmlAttr(seo.canonical)}" />`,
  ];

  if (seo.keywords) {
    lines.push(
      `<meta name="keywords" content="${escapeHtmlAttr(seo.keywords)}" />`
    );
  }

  lines.push(
    `<meta property="og:title" content="${escapeHtmlAttr(seo.og.title)}" />`,
    `<meta property="og:description" content="${escapeHtmlAttr(
      seo.og.description
    )}" />`,
    `<meta property="og:url" content="${escapeHtmlAttr(seo.og.url)}" />`,
    `<meta property="og:type" content="${escapeHtmlAttr(seo.og.type)}" />`
  );

  if (seo.og.siteName) {
    lines.push(
      `<meta property="og:site_name" content="${escapeHtmlAttr(
        seo.og.siteName
      )}" />`
    );
  }
  if (seo.og.locale) {
    lines.push(
      `<meta property="og:locale" content="${escapeHtmlAttr(seo.og.locale)}" />`
    );
  }
  if (seo.og.image) {
    lines.push(
      `<meta property="og:image" content="${escapeHtmlAttr(seo.og.image)}" />`
    );
  }
  if (seo.og.imageSecureUrl) {
    lines.push(
      `<meta property="og:image:secure_url" content="${escapeHtmlAttr(
        seo.og.imageSecureUrl
      )}" />`
    );
  }
  if (seo.og.imageWidth) {
    lines.push(
      `<meta property="og:image:width" content="${escapeHtmlAttr(
        seo.og.imageWidth
      )}" />`
    );
  }
  if (seo.og.imageHeight) {
    lines.push(
      `<meta property="og:image:height" content="${escapeHtmlAttr(
        seo.og.imageHeight
      )}" />`
    );
  }

  lines.push(
    `<meta name="twitter:card" content="${escapeHtmlAttr(
      seo.twitter.card
    )}" />`,
    `<meta name="twitter:title" content="${escapeHtmlAttr(
      seo.twitter.title
    )}" />`,
    `<meta name="twitter:description" content="${escapeHtmlAttr(
      seo.twitter.description
    )}" />`
  );

  if (seo.twitter.image) {
    lines.push(
      `<meta name="twitter:image" content="${escapeHtmlAttr(
        seo.twitter.image
      )}" />`
    );
  }

  if (seo.jsonLd) {
    lines.push(
      `<script type="application/ld+json">${safeJsonForScript(
        seo.jsonLd
      )}</script>`
    );
  }

  return lines.join('\n');
}

export function normalizeText(text: string): string {
  return String(text || '')
    .replace(/[\n\t\r]+/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function truncateWords(text: string, maxChars: number): string {
  const normalized = normalizeText(text);
  if (!normalized || normalized.length <= maxChars) return normalized;
  if (maxChars <= 3) return '...'.slice(0, Math.max(0, maxChars));

  const limit = maxChars - 3;
  const words = normalized.split(' ');
  let output = '';

  for (const word of words) {
    const next = output ? `${output} ${word}` : word;
    if (next.length > limit) break;
    output = next;
  }

  if (!output) {
    return `${normalized.slice(0, limit).trim()}...`;
  }

  return `${output}...`;
}

export function escapeHtmlAttr(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function buildCanonical(reqContext: SeoRequestContext): string {
  const safeOriginalUrl = reqContext.originalUrl || reqContext.pathname || '/';
  const [rawPath, rawQuery = ''] = safeOriginalUrl.split('#')[0].split('?');
  const query = new URLSearchParams(rawQuery);
  for (const key of Array.from(query.keys())) {
    if (isTrackingKey(key)) {
      query.delete(key);
    }
  }

  let path = rawPath.split('#')[0] || '/';
  path = path.replace(/^\/(?:e|event)\//, '/evento/');
  path = path.replace(/\/$/, '') || '/';
  // These routes do not implement query-driven pagination/content variants.
  // Credentials and transaction identifiers must never enter head metadata.
  if (/^\/(?:tickets|r)\//.test(path))
    path = path.split('/').slice(0, 2).join('/');
  if (/^\/checkout\/confirm\//.test(path)) path = '/checkout/confirm';
  const knownRoute =
    /^(?:\/|\/(?:eventos|evento|tickets|r|baixar-ingressos|checkout|login|register|invite|account|online-access|live)(?:\/.*)?|\/(?:sobre|ajuda|contato|organizadores|perguntas-frequentes|politica-de-privacidade|termos-de-uso|preferencias-de-cookies))$/.test(
      path
    );
  const cleanQuery = knownRoute ? '' : query.toString();
  const origin = configuredSiteOrigin(
    `${reqContext.protocol}://${reqContext.host}`
  );
  path = new URL(`${origin}/${path.replace(/^\/+/, '')}`).pathname;
  const suffix = cleanQuery ? `?${cleanQuery}` : '';
  return `${origin}${path}${suffix}`;
}

function buildSeoHead(params: {
  title: string;
  description: string;
  canonical: string;
  keywords?: string;
  robots: string;
  ogType: OgType;
  image?: string;
  jsonLd?: Record<string, unknown> | null;
}): SeoHead {
  const title = normalizeText(params.title);
  const description = normalizeText(params.description);
  const image = params.image ? normalizeText(params.image) : undefined;
  const twitterCard: TwitterCard = image ? 'summary_large_image' : 'summary';

  return {
    title,
    description,
    canonical: params.canonical,
    keywords: params.keywords,
    robots: params.robots,
    og: {
      title,
      description,
      url: params.canonical,
      type: params.ogType,
      image,
      imageSecureUrl: image,
      imageWidth: image ? '1200' : undefined,
      imageHeight: image ? '630' : undefined,
      siteName: 'AccessHub',
      locale: 'pt_BR',
    },
    twitter: {
      card: twitterCard,
      title,
      description,
      image,
    },
    jsonLd: params.jsonLd ?? null,
  };
}

function buildEventTitle(
  name: string,
  cityOrPlace: string,
  dateLabel: string
): string {
  const eventName = normalizeText(name || 'Evento');
  const place = normalizeText(cityOrPlace || '');
  const date = normalizeText(dateLabel || '');
  const parts = [place ? `em ${place}` : '', date ? `- ${date}` : '']
    .filter(Boolean)
    .join(' ');

  const candidates = [
    `${eventName}${parts ? ` ${parts}` : ''} | AccessHub`,
    `${eventName}${place ? ` em ${place}` : ''} | AccessHub`,
  ];
  // Prefer concise context, but never discard the actual event name to hit a quota.
  return (
    candidates.find((candidate) => candidate.length <= 60) ||
    `${eventName} | AccessHub`
  );
}

function buildEventDescription(
  name: string,
  cityOrPlace: string,
  dateLabel: string
): string {
  const details = [normalizeText(name || 'Evento')];
  const place = normalizeText(cityOrPlace);
  const date = normalizeText(dateLabel);
  if (place) details.push(`Local: ${place}`);
  if (date) details.push(`Data: ${date}`);
  details.push('Confira informações sobre o evento e a participação.');
  return truncateWords(details.join('. '), 160);
}

function buildListingTitle(city: string, category: string): string {
  if (city && category)
    return truncateWords(
      `Eventos de ${category} em ${city} | Ingressos AccessHub`,
      60
    );
  if (city)
    return truncateWords(`Eventos em ${city} | Ingressos AccessHub`, 60);
  return 'Eventos e ingressos online | AccessHub';
}

function buildListingDescription(city: string, category: string): string {
  if (city && category) {
    return truncateWords(
      `Encontre ${category} em ${city}. Veja datas, horarios e compre ingressos online com seguranca no AccessHub.`,
      160
    );
  }
  if (city) {
    return truncateWords(
      `Explore eventos em ${city}. Compare opcoes, confira detalhes e garanta seus ingressos online no AccessHub.`,
      160
    );
  }
  return truncateWords(
    'Descubra eventos em varias cidades, confira informacoes atualizadas e compre ingressos online com praticidade no AccessHub.',
    160
  );
}

function buildKeywords(params: {
  eventName: string;
  city: string;
  category: string;
  location: string;
}): string {
  const terms: string[] = [
    'ingressos',
    'eventos',
    'AccessHub',
    'ingresso online',
  ];
  const nameTerms = keywordCandidatesFromEventName(params.eventName);
  if (params.city) terms.push(params.city);
  if (params.category) terms.push(params.category);
  if (params.location && params.location !== params.city)
    terms.push(params.location);
  terms.push(...nameTerms.slice(0, 6));

  const unique = Array.from(
    new Set(terms.map((item) => normalizeText(item)).filter(Boolean))
  );
  return unique.slice(0, 12).join(', ');
}

function keywordCandidatesFromEventName(name: string): string[] {
  const normalized = normalizeText(name)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return normalized
    .split(/[^a-zA-Z0-9]+/)
    .map((token) => token.toLowerCase())
    .filter((token) => token.length >= 3 && !STOPWORDS.has(token));
}

function resolveImage(eventData: SeoEventData): string {
  return normalizeText(
    eventData.coverThumbLg ||
      eventData.coverThumbMd ||
      eventData.coverThumbs?.lg ||
      eventData.coverImageUrl ||
      eventData.coverThumbSm ||
      eventData.coverThumbSquare ||
      ''
  );
}

export function buildOgImage(
  value: string,
  options?: {
    headline?: string;
    subtitle?: string;
    cta?: string;
  }
): string | null {
  const source = normalizeText(value);
  if (!safeSocialImage(source, DEFAULT_SITE_ORIGIN)) return null;
  if (!source) return null;
  if (!/res\.cloudinary\.com\/.+\/image\/upload\//i.test(source)) return null;

  const marker = '/upload/';
  const markerIndex = source.indexOf(marker);
  if (markerIndex < 0) return null;

  const prefix = source.slice(0, markerIndex + marker.length);
  const rest = source.slice(markerIndex + marker.length).replace(/^\/+/, '');
  let assetPath = '';
  const explicitVersion = /(?:^|\/)(v\d+\/.+)$/i.exec(rest);
  if (explicitVersion?.[1]) {
    assetPath = explicitVersion[1];
  } else {
    const segments = rest.split('/').filter(Boolean);
    while (segments.length > 1 && isCloudinaryTransformSegment(segments[0])) {
      segments.shift();
    }
    assetPath = segments.join('/');
  }

  if (!assetPath) return null;

  const transformParts = ['w_1200,h_630,c_fill,g_auto,ar_1.91,f_jpg,q_auto'];
  const headline = truncateWords(normalizeText(options?.headline || ''), 56);
  const subtitle = truncateWords(normalizeText(options?.subtitle || ''), 64);
  const cta = normalizeText(options?.cta || 'Compre ingressos');

  if (headline) {
    transformParts.push(
      `l_text:Arial_58_bold:${encodeCloudinaryText(
        headline
      )},co_rgb:FFFFFF,g_north_west,x_72,y_72`
    );
  }
  if (subtitle) {
    transformParts.push(
      `l_text:Arial_34:${encodeCloudinaryText(
        subtitle
      )},co_rgb:E2E8F0,g_north_west,x_72,y_150`
    );
  }
  transformParts.push(
    `l_text:Arial_42_bold:${encodeCloudinaryText(
      cta
    )},co_rgb:FFFFFF,b_rgb:F97316,bo_18px_solid_rgb:F97316,g_south_west,x_72,y_54`
  );

  const transform = transformParts.join('/');
  return forceHttps(`${prefix}${transform}/${assetPath}`);
}

function isCloudinaryTransformSegment(segment: string): boolean {
  if (!segment) return false;
  if (segment.includes(',')) return true;
  return /^[a-z]{1,4}_.+/i.test(segment);
}

function toAbsoluteUrl(value: string, reqContext: SeoRequestContext): string {
  const url = normalizeText(value);
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  if (url.startsWith('//')) return `${reqContext.protocol}:${url}`;
  return `${reqContext.protocol}://${reqContext.host}${
    url.startsWith('/') ? url : `/${url}`
  }`;
}

function resolveEventImageUrl(
  value: string,
  reqContext: SeoRequestContext,
  options?: { headline?: string; subtitle?: string; cta?: string }
): string {
  const url = normalizeText(value);
  if (!url) return '';
  if (!safeSocialImage(url, `${reqContext.protocol}://${reqContext.host}`))
    return '';

  const cloudinary = buildOgImage(url, options);
  if (cloudinary) return cloudinary;

  if (/^https?:\/\//i.test(url)) return forceHttps(url);
  if (url.startsWith('//')) return forceHttps(`${reqContext.protocol}:${url}`);
  if (url.startsWith('/uploads/') && reqContext.apiOrigin) {
    return forceHttps(`${reqContext.apiOrigin.replace(/\/$/, '')}${url}`);
  }

  return forceHttps(toAbsoluteUrl(url, reqContext));
}

function encodeCloudinaryText(value: string): string {
  return encodeURIComponent(value).replace(/%20/g, '%20');
}

function buildFallbackOgImageUrl(
  slug: string,
  reqContext: SeoRequestContext
): string {
  const safeSlug = normalizeText(slug || '').replace(/[^a-zA-Z0-9-]/g, '');
  if (!safeSlug) return '';

  const base = normalizeText(
    reqContext.ogImageFallbackBaseUrl ||
      `${reqContext.protocol}://${reqContext.host}`
  ).replace(/\/$/, '');
  if (!base) return '';
  return forceHttps(
    `${base}/public/seo/og/event/${encodeURIComponent(safeSlug)}.svg`
  );
}

function forceHttps(url: string): string {
  if (!url) return '';
  return url.replace(/^http:\/\//i, 'https://');
}

function formatDateShort(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return normalizeText(
    new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      timeZone: 'America/Sao_Paulo',
    }).format(date)
  );
}

function stripHtml(value: string): string {
  return normalizeText(
    String(value || '')
      .replace(/<script[\s\S]*?<\/script>/gi, '')
      .replace(/<style[\s\S]*?<\/style>/gi, '')
      .replace(/<\/?[^>]+(>|$)/g, ' ')
      .replace(/&nbsp;/gi, ' ')
  );
}

function buildEventJsonLd(
  eventData: SeoEventData,
  canonical: string,
  image?: string
): Record<string, unknown> {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: normalizeText(eventData.name || 'Evento'),
    ...(eventData.description
      ? { description: truncateWords(stripHtml(eventData.description), 500) }
      : {}),
    url: canonical,
  };

  if (eventData.startAt && Number.isFinite(Date.parse(eventData.startAt))) {
    schema['startDate'] = eventData.startAt;
  }
  if (eventData.endAt && Number.isFinite(Date.parse(eventData.endAt))) {
    schema['endDate'] = eventData.endAt;
  }

  const attendance = sanitizeAttendanceMode(
    eventData.attendanceMode,
    eventData.type
  );
  if (attendance) {
    schema['eventAttendanceMode'] = attendance;
  }

  const eventStatus = sanitizeEventStatus(
    eventData.eventStatus,
    eventData.status
  );
  if (eventStatus) {
    schema['eventStatus'] = eventStatus;
  }

  if (image) {
    schema['image'] = [image];
  }

  const location = buildLocation(eventData);
  if (location) {
    schema['location'] = location;
  }

  const organizer = buildOrganizer(eventData);
  if (organizer) {
    schema['organizer'] = organizer;
  }

  const offers = buildOffers(eventData, canonical);
  if (offers) {
    schema['offers'] = offers;
  }

  return schema;
}

function buildLocation(
  eventData: SeoEventData
): Record<string, unknown> | null {
  // No public virtual URL is provided; never expose protected online access URLs.
  if (eventData.type === 'ONLINE') return null;
  const placeName = normalizeText(
    eventData.location || eventData.address || ''
  );
  const streetAddress = cleanAddressField(eventData.address);
  const locality = cleanAddressField(eventData.city);
  const region = cleanAddressField(eventData.state);
  const postalCode = cleanAddressField(eventData.zipcode);
  const country = cleanAddressField(eventData.country);

  const address: Record<string, unknown> = { '@type': 'PostalAddress' };
  if (streetAddress) address['streetAddress'] = streetAddress;
  if (locality) address['addressLocality'] = locality;
  if (region) address['addressRegion'] = region;
  if (postalCode) address['postalCode'] = postalCode;
  if (country) address['addressCountry'] = country;

  const hasAddressData = Object.keys(address).length > 1;
  if (!placeName && !hasAddressData) return null;

  const place: Record<string, unknown> = { '@type': 'Place' };
  if (placeName) place['name'] = placeName;
  if (hasAddressData) place['address'] = address;
  return place;
}

function buildOrganizer(
  eventData: SeoEventData
): Record<string, unknown> | null {
  const organizerName = cleanAddressField(eventData.organizerName);
  const organizerUrl = normalizeText(eventData.organizerUrl || '');
  if (!organizerName) return null;

  const organizer: Record<string, unknown> = {
    '@type': 'Organization',
    name: organizerName,
  };
  if (organizerUrl && /^https?:\/\//i.test(organizerUrl)) {
    organizer['url'] = organizerUrl;
  }
  return organizer;
}

function buildOffers(
  eventData: SeoEventData,
  canonical: string
): Record<string, unknown> | null {
  const priced = (eventData.batches || []).filter(
    (batch) =>
      batch.price !== null &&
      batch.price !== undefined &&
      Number.isFinite(Number(batch.price)) &&
      Number(batch.price) >= 0
  );
  if (!priced.length) return null;
  const now = Date.now();
  const available = priced.filter(
    (batch) =>
      batch.status === 'active' &&
      (!batch.startsAt || Date.parse(batch.startsAt) <= now) &&
      (!batch.endsAt || Date.parse(batch.endsAt) >= now) &&
      batch.quantity !== null &&
      batch.quantity !== undefined &&
      batch.sold !== null &&
      batch.sold !== undefined &&
      Number.isFinite(Number(batch.quantity)) &&
      Number.isFinite(Number(batch.sold)) &&
      Number(batch.quantity) > Number(batch.sold)
  );
  const selected = available.length ? available : priced;
  const soldOut = priced.every(
    (batch) =>
      batch.status === 'sold_out' ||
      (batch.quantity !== null &&
        batch.quantity !== undefined &&
        batch.sold !== null &&
        batch.sold !== undefined &&
        Number.isFinite(Number(batch.quantity)) &&
        Number.isFinite(Number(batch.sold)) &&
        Number(batch.quantity) <= Number(batch.sold))
  );
  return {
    '@type': 'Offer',
    priceCurrency: 'BRL',
    price: Math.min(...selected.map((batch) => Number(batch.price))).toFixed(2),
    ...(available.length
      ? { availability: 'https://schema.org/InStock' }
      : soldOut
      ? { availability: 'https://schema.org/SoldOut' }
      : {}),
    url: canonical,
  };
}

function cleanAddressField(value?: string | null): string | undefined {
  const text = normalizeText(value || '');
  if (!text) return undefined;
  const onlyLetters = text.replace(/[^a-zA-Z]/g, '');
  if (onlyLetters.length > 0 && onlyLetters.length < 2) return undefined;
  if (text.length < 2) return undefined;
  return text;
}

function sanitizeAttendanceMode(
  attendanceMode?: string | null,
  type?: string | null
): string | undefined {
  const normalized = normalizeText(attendanceMode || '');
  if (normalized.startsWith('https://schema.org/')) return normalized;
  const eventType = normalizeText(type || '').toUpperCase();
  if (eventType === 'ONLINE')
    return 'https://schema.org/OnlineEventAttendanceMode';
  if (eventType === 'HYBRID')
    return 'https://schema.org/MixedEventAttendanceMode';
  if (eventType === 'PRESENTIAL')
    return 'https://schema.org/OfflineEventAttendanceMode';
  return undefined;
}

function sanitizeEventStatus(
  eventStatus?: string | null,
  status?: string
): string | undefined {
  const normalized = normalizeText(eventStatus || '');
  if (normalized.startsWith('https://schema.org/')) return normalized;
  if (status === 'published') return 'https://schema.org/EventScheduled';
  if (status === 'archived') return 'https://schema.org/EventCancelled';
  if (status === 'draft') return 'https://schema.org/EventPostponed';
  return undefined;
}

export function safeJsonForScript(value: object): string {
  return JSON.stringify(value)
    .replace(/</g, '\\u003c')
    .replace(/\u2028/g, '\\u2028')
    .replace(/\u2029/g, '\\u2029');
}
